import React, {
  useEffect,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import "./AscendInstallment.css";

// =====================================================
// ASCEND CONFIG
// =====================================================

const SERVICE_NAME =
  "Forge Your Peak - Ascend";

const PAYMENT_PLAN =
  "3_INSTALLMENTS";

const OFFER_CODE =
  "ascend_3_installments";

// OFFER TOTAL = ₹45,000
const TOTAL_PRICE = 45000;

// EACH INSTALLMENT = ₹15,000
const INSTALLMENT_AMOUNT = 15000;

const TOTAL_INSTALLMENTS = 3;

// ORIGINAL PRICE = ₹49,999
const ORIGINAL_PRICE = 49999;

// META PIXEL
const META_PIXEL_ID =
  "2060088607979588";

// =====================================================
// COMPONENT
// =====================================================

const AscendInstallment = () => {
  const navigate = useNavigate();

  // ===================================================
  // FORM
  // ===================================================

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
  });

  // ===================================================
  // INSTALLMENT STATUS
  // ===================================================

  const [installmentData, setInstallmentData] =
    useState(null);

  const [isCheckingInstallment, setIsCheckingInstallment] =
    useState(false);

  const [isLoading, setIsLoading] =
    useState(false);

  const [statusMessage, setStatusMessage] =
    useState("");

  const [statusType, setStatusType] =
    useState("");

  // ===================================================
  // HIDE NAVBAR / FOOTER
  // ===================================================

  useEffect(() => {
    window.scrollTo(0, 0);

    const navbar =
      document.querySelector(
        "header, .navbar"
      );

    const footer =
      document.querySelector("footer");

    if (navbar) {
      navbar.style.display = "none";
    }

    if (footer) {
      footer.style.display = "none";
    }

    return () => {
      if (navbar) {
        navbar.style.display = "";
      }

      if (footer) {
        footer.style.display = "";
      }
    };
  }, []);

  // ===================================================
  // INPUT CHANGE
  // ===================================================

  const handleChange = (e) => {
    const {
      name,
      value,
    } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (name === "email") {
      setInstallmentData(null);
    }

    setStatusMessage("");
    setStatusType("");
  };

  // ===================================================
  // PHONE CHANGE
  // ===================================================

  const handlePhoneChange = (phone) => {
    setFormData((prev) => ({
      ...prev,
      phone,
    }));

    setInstallmentData(null);

    setStatusMessage("");
    setStatusType("");
  };

  // ===================================================
  // VALIDATE CUSTOMER DETAILS
  // ===================================================

  const validateCustomerDetails = () => {
    const name =
      formData.name.trim();

    const email =
      formData.email
        .trim()
        .toLowerCase();

    const phone =
      formData.phone.trim();

    if (!name) {
      return "Please enter your full name.";
    }

    if (name.length < 2) {
      return "Please enter a valid name.";
    }

    if (!email) {
      return "Please enter your email address.";
    }

    const emailRegex =
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(email)) {
      return "Please enter a valid email address.";
    }

    if (!phone) {
      return "Please enter your phone number.";
    }

    const digits =
      phone.replace(/\D/g, "");

    if (digits.length < 10) {
      return "Please enter a valid phone number.";
    }

    return null;
  };

  // ===================================================
  // CHECK INSTALLMENT
  // ===================================================

  const checkInstallment = async () => {
    setStatusMessage("");
    setStatusType("");

    const validationError =
      validateCustomerDetails();

    if (validationError) {
      setStatusMessage(
        validationError
      );

      setStatusType("error");

      return;
    }

    try {
      setIsCheckingInstallment(true);

      const email =
        formData.email
          .trim()
          .toLowerCase();

      const response =
        await axios.post(
          "/api/razorpay/installment-status",
          {
            email,
          }
        );

      const data =
        response.data;

      if (!data?.success) {
        throw new Error(
          data?.message ||
            "Unable to check installment status."
        );
      }

      setInstallmentData(data);

      if (data.completed) {
        setStatusMessage(
          "All 3 instalments have already been completed."
        );

        setStatusType("success");

        return;
      }

      setStatusMessage(
        `Instalment ${data.nextInstallment} is available for payment.`
      );

      setStatusType("success");
    } catch (error) {
      console.error(
        "❌ Installment status error:",
        error
      );

      setInstallmentData(null);

      setStatusMessage(
        error.response?.data?.message ||
          error.message ||
          "Unable to check installment status."
      );

      setStatusType("error");
    } finally {
      setIsCheckingInstallment(false);
    }
  };

  // ===================================================
  // META PURCHASE
  //
  // ONLY ₹15,000 SUCCESSFUL PAYMENT
  // Name / Email / Phone are NOT sent.
  // ===================================================

 const trackMetaPurchase = (eventId) => {
  try {
    if (
      typeof window === "undefined" ||
      typeof window.fbq !== "function"
    ) {
      console.warn(
        "⚠️ Meta Pixel is not available."
      );

      return;
    }

    window.fbq(
      "trackSingle",
      META_PIXEL_ID,
      "Purchase",
      {
        content_name:
          SERVICE_NAME,

        content_category:
          "Ascend",

        value:
          INSTALLMENT_AMOUNT,

        currency:
          "INR",
      },
      {
        eventID: eventId,
      }
    );

    console.log(
      "✅ Meta Purchase event sent:",
      {
        service: SERVICE_NAME,
        value: INSTALLMENT_AMOUNT,
        currency: "INR",
        eventID: eventId,
      }
    );
  } catch (error) {
    console.error(
      "❌ Meta Purchase event error:",
      error
    );
  }
};
  // ===================================================
  // HANDLE PAYMENT
  // ===================================================

  const handlePayment = async () => {
    setStatusMessage("");
    setStatusType("");

    const validationError =
      validateCustomerDetails();

    if (validationError) {
      setStatusMessage(
        validationError
      );

      setStatusType("error");

      return;
    }

    // -------------------------------------------------
    // MUST CHECK INSTALLMENT FIRST
    // -------------------------------------------------

    if (!installmentData) {
      setStatusMessage(
        "Please check your installment status before proceeding to payment."
      );

      setStatusType("error");

      return;
    }

    if (
      installmentData.completed
    ) {
      setStatusMessage(
        "All 3 instalments have already been completed."
      );

      setStatusType("success");

      return;
    }

    // -------------------------------------------------
    // RAZORPAY CHECK
    // -------------------------------------------------

    if (!window.Razorpay) {
      setStatusMessage(
        "Razorpay checkout is not loaded. Please refresh the page and try again."
      );

      setStatusType("error");

      return;
    }

    try {
      setIsLoading(true);

      const email =
        formData.email
          .trim()
          .toLowerCase();

      const name =
        formData.name.trim();

      const phone =
        formData.phone.trim();

      // ------------------------------------------------
      // RECHECK FROM SERVER
      // ------------------------------------------------

      const statusResponse =
        await axios.post(
          "/api/razorpay/installment-status",
          {
            email,
          }
        );

      const latestStatus =
        statusResponse.data;

      if (!latestStatus?.success) {
        throw new Error(
          latestStatus?.message ||
            "Unable to verify installment status."
        );
      }

      if (
        latestStatus.completed
      ) {
        setInstallmentData(
          latestStatus
        );

        setStatusMessage(
          "All 3 instalments have already been completed."
        );

        setStatusType("success");

        setIsLoading(false);

        return;
      }

      const nextInstallment =
        Number(
          latestStatus.nextInstallment
        );

      if (
        ![1, 2, 3].includes(
          nextInstallment
        )
      ) {
        throw new Error(
          "Invalid installment status received from server."
        );
      }

      setInstallmentData(
        latestStatus
      );

      // ------------------------------------------------
      // CREATE RAZORPAY ORDER
      // ------------------------------------------------

      const response =
        await axios.post(
          "/api/razorpay/create-order",
          {
            name,

            email,

            phone,

            service:
              SERVICE_NAME,

            offer:
              OFFER_CODE,

            paymentPlan:
              PAYMENT_PLAN,

            installmentNumber:
              nextInstallment,

            totalInstallments:
              TOTAL_INSTALLMENTS,

            totalAmount:
              TOTAL_PRICE,
          }
        );

      const data =
        response.data;

      console.log(
        "✅ Ascend installment order response:",
        data
      );

      if (!data?.success) {
        throw new Error(
          data?.message ||
            "Unable to create payment order."
        );
      }

      if (
        !data.order?.id
      ) {
        throw new Error(
          "Razorpay order ID was not received."
        );
      }

      // ------------------------------------------------
      // SERVER AMOUNT CHECK
      // ------------------------------------------------

      if (
        Number(
          data.order.amount
        ) !==
        INSTALLMENT_AMOUNT * 100
      ) {
        throw new Error(
          "Invalid installment amount received from server."
        );
      }

      // ------------------------------------------------
      // RAZORPAY OPTIONS
      // ------------------------------------------------

      const options = {
        key:
          data.keyId,

        amount:
          data.order.amount,

        currency:
          data.order.currency ||
          "INR",

        name:
          "Forge Your Peak",

        description:
          `Forge Your Peak - Ascend | Instalment ${nextInstallment} of ${TOTAL_INSTALLMENTS}`,

        order_id:
          data.order.id,
        config: {
            display: {
              hide: [
                {
                  method: "emi",
                },
              ],

              preferences: {
                show_default_blocks: true,
              },
            },
          },

        prefill: {
          name,

          email,

          contact:
            phone,
        },

        notes: {
          service:
            SERVICE_NAME,

          paymentPlan:
            PAYMENT_PLAN,

          installmentNumber:
            String(
              nextInstallment
            ),

          totalInstallments:
            String(
              TOTAL_INSTALLMENTS
            ),

          totalAmount:
            String(
              TOTAL_PRICE
            ),

          originalAmount:
            String(
              ORIGINAL_PRICE
            ),
        },

        theme: {
          color:
            "#20264F",
        },

        modal: {
          ondismiss: () => {
            console.log(
              "Razorpay checkout closed."
            );

            setIsLoading(false);
          },
        },

        // ==============================================
        // PAYMENT SUCCESS
        // ==============================================

        handler:
          async (
            paymentResponse
          ) => {
            try {
              console.log(
                "✅ Razorpay payment response:",
                paymentResponse
              );

              // ------------------------------------------
              // VERIFY PAYMENT SIGNATURE
              // ------------------------------------------

              const verifyResponse =
                await axios.post(
                  "/api/razorpay/verify-payment",
                  {
                    razorpay_order_id:
                      paymentResponse.razorpay_order_id,

                    razorpay_payment_id:
                      paymentResponse.razorpay_payment_id,

                    razorpay_signature:
                      paymentResponse.razorpay_signature,
                  }
                );

              const verifyData =
                verifyResponse.data;

              console.log(
                "✅ Payment verification response:",
                verifyData
              );

              if (
                !verifyData?.success
              ) {
                throw new Error(
                  verifyData?.message ||
                    "Payment verification failed."
                );
              }

              // ==========================================
              // META PURCHASE
              //
              // ₹15,000 payment successful.
              // No name/email/phone sent to Meta.
              // ==========================================

              trackMetaPurchase(
  paymentResponse.razorpay_payment_id
);

              // ------------------------------------------
              // SUCCESS UI
              // ------------------------------------------

              setStatusMessage(
                `Instalment ${nextInstallment} payment successful.`
              );

              setStatusType(
                "success"
              );

              setIsLoading(false);

              // ------------------------------------------
              // DATABASE ACTIVATION
              //
              // Existing backend webhook handles DB.
              // ------------------------------------------

              navigate(
                "/dashboard-login",
                {
                  replace: true,
                }
              );
            } catch (error) {
              console.error(
                "❌ Payment verification error:",
                error
              );

              setIsLoading(false);

              setStatusMessage(
                error.response
                  ?.data?.message ||
                  error.message ||
                  "Payment verification failed. Please contact support."
              );

              setStatusType(
                "error"
              );
            }
          },
      };

      // ------------------------------------------------
      // CREATE RAZORPAY INSTANCE
      // ------------------------------------------------

      const razorpay =
        new window.Razorpay(
          options
        );

      // ------------------------------------------------
      // PAYMENT FAILED
      // ------------------------------------------------

      razorpay.on(
        "payment.failed",
        (response) => {
          console.error(
            "❌ Razorpay payment failed:",
            response?.error
          );

          setIsLoading(false);

          setStatusMessage(
            response?.error
              ?.description ||
              "Payment failed. Please try again."
          );

          setStatusType(
            "error"
          );
        }
      );

      // ------------------------------------------------
      // OPEN CHECKOUT
      // ------------------------------------------------

      razorpay.open();
    } catch (error) {
      console.error(
        "❌ Ascend payment initialization error:",
        error
      );

      setIsLoading(false);

      setStatusMessage(
        error.response?.data
          ?.message ||
          error.message ||
          "Something went wrong while starting the payment."
      );

      setStatusType("error");
    }
  };

  // ===================================================
  // FORMAT MONEY
  // ===================================================

  const formatINR = (amount) => {
    return Number(
      amount || 0
    ).toLocaleString(
      "en-IN"
    );
  };

  // ===================================================
  // CURRENT INSTALLMENT
  // ===================================================

  const nextInstallment =
    Number(
      installmentData?.nextInstallment ||
        0
    );

  const isCompleted =
    installmentData?.completed ===
    true;

  // ===================================================
  // RENDER
  // ===================================================

  return (
    <div className="ascend-payment-page">

      {/* =========================================
          LEFT
      ========================================= */}

      <section className="ascend-payment-left">

        <div className="ascend-left-inner">

          <div className="ascend-company-logo">
            <img
              src="/Logo.png"
              alt="Forge Your Peak"
            />
          </div>

          <div className="ascend-product-header">

            <h1>
              Forge Your Peak – Ascend
            </h1>

            <p>
              By ForgeYourPeak
            </p>

            <div className="ascend-product-price">

              <span className="ascend-original-price">
                ₹{formatINR(ORIGINAL_PRICE)}
              </span>

              <span className="ascend-offer-price">
                ₹{formatINR(TOTAL_PRICE)}
              </span>

            </div>

          </div>

          <div className="ascend-product-description">

            <p className="ascend-intro">
              A 90-day personalized
              neuro-performance coaching
              program for athletes who want
              direct guidance, accountability,
              and a customized roadmap.
            </p>

            <p>
              While Ignite teaches the framework,
              Ascend adapts it to your sport,
              position, strengths, weaknesses,
              and competitive goals.
            </p>

            <p>
              Through weekly 1-on-1 coaching
              sessions, continuous assessment,
              and personalized training
              prescriptions, you'll focus on
              the specific performance
              bottlenecks holding you back and
              develop the skills that matter
              most for your game.
            </p>

            <h2>
              What is included?
            </h2>

            <ul>
              <li>
                Weekly 1-on-1 coaching sessions
              </li>

              <li>
                Personalized performance roadmap
              </li>

              <li>
                Complete Ignite curriculum
              </li>

              <li>
                Continuous performance assessment
              </li>

              <li>
                Personalized training prescriptions
              </li>

              <li>
                Direct accountability and guidance
              </li>
            </ul>

          </div>

        </div>

      </section>

      {/* =========================================
          RIGHT
      ========================================= */}

      <section className="ascend-payment-right">

        <div className="ascend-payment-right-inner">

          <h2>
            Payment details
          </h2>

          <p className="ascend-payment-subtitle">
            Enter your details to check your
            instalment and continue securely.
          </p>

          {/* BILLING */}

          <div className="ascend-billing-card">

            <h3>
              Billing information
            </h3>

            <div className="ascend-billing-fields">

              <input
                type="text"
                name="name"
                placeholder="Name"
                value={
                  formData.name
                }
                onChange={
                  handleChange
                }
                disabled={
                  isLoading ||
                  isCheckingInstallment
                }
                autoComplete="name"
              />

              <input
                type="email"
                name="email"
                placeholder="Email"
                value={
                  formData.email
                }
                onChange={
                  handleChange
                }
                disabled={
                  isLoading ||
                  isCheckingInstallment
                }
                autoComplete="email"
              />

              <div className="ascend-phone-wrapper">

                <PhoneInput
                  country="in"
                  value={
                    formData.phone
                  }
                  onChange={
                    handlePhoneChange
                  }
                  disabled={
                    isLoading ||
                    isCheckingInstallment
                  }
                  enableSearch
                  containerClass="ascend-phone-container"
                  inputClass="ascend-phone-input"
                  buttonClass="ascend-phone-button"
                  dropdownClass="ascend-phone-dropdown"
                />

              </div>

            </div>

          </div>

          {/* CHECK INSTALLMENT */}

          <button
            type="button"
            className="ascend-pay-button"
            onClick={
              checkInstallment
            }
            disabled={
              isLoading ||
              isCheckingInstallment
            }
          >
            {isCheckingInstallment
              ? "Checking..."
              : "Check Instalment"}
          </button>

          {/* STATUS */}

          {statusMessage && (
            <div
              style={{
                marginTop: "16px",
                padding: "13px 15px",
                borderRadius: "7px",
                fontSize: "14px",
                lineHeight: "1.5",
                background:
                  statusType ===
                  "error"
                    ? "#fef2f2"
                    : "#f0fdf4",
                color:
                  statusType ===
                  "error"
                    ? "#991b1b"
                    : "#166534",
                border:
                  statusType ===
                  "error"
                    ? "1px solid #fecaca"
                    : "1px solid #bbf7d0",
              }}
            >
              {statusMessage}
            </div>
          )}

          {/* INSTALLMENT STATUS */}

          {installmentData && (
            <>

              {!isCompleted && (
                <div
                  style={{
                    marginTop: "20px",
                    background: "#fff",
                    border: "1px solid #dfe3e8",
                    borderRadius: "7px",
                    padding: "18px",
                  }}
                >

                  <h3
                    style={{
                      margin:
                        "0 0 16px",
                      fontSize:
                        "18px",
                      color:
                        "#111827",
                    }}
                  >
                    Payment schedule
                  </h3>

                  <div
                    style={{
                      display:
                        "grid",
                      gridTemplateColumns:
                        "repeat(3, 1fr)",
                      gap: "8px",
                    }}
                  >

                    {[1, 2, 3].map(
                      (number) => {

                        const paid =
                          number <
                          nextInstallment;

                        const current =
                          number ===
                          nextInstallment;

                        return (
                          <div
                            key={
                              number
                            }
                            style={{
                              textAlign:
                                "center",
                              fontSize:
                                "12px",
                              color:
                                paid ||
                                current
                                  ? "#111827"
                                  : "#94a3b8",
                            }}
                          >

                            <div
                              style={{
                                width:
                                  "34px",
                                height:
                                  "34px",
                                margin:
                                  "0 auto 7px",
                                borderRadius:
                                  "50%",
                                display:
                                  "flex",
                                alignItems:
                                  "center",
                                justifyContent:
                                  "center",
                                border:
                                  paid
                                    ? "1px solid #16a34a"
                                    : current
                                    ? "1px solid #20264f"
                                    : "1px solid #d1d5db",
                                background:
                                  paid
                                    ? "#f0fdf4"
                                    : "#fff",
                                color:
                                  paid
                                    ? "#166534"
                                    : current
                                    ? "#20264f"
                                    : "#64748b",
                                fontWeight:
                                  "700",
                              }}
                            >
                              {paid
                                ? "✓"
                                : number}
                            </div>

                            Instalment{" "}
                            {number}

                          </div>
                        );
                      }
                    )}

                  </div>

                  <div
                    style={{
                      marginTop:
                        "20px",
                      paddingTop:
                        "18px",
                      borderTop:
                        "1px dashed #d5dae1",
                    }}
                  >

                    <div
                      style={{
                        display:
                          "flex",
                        justifyContent:
                          "space-between",
                        alignItems:
                          "center",
                        gap:
                          "15px",
                      }}
                    >

                      <span
                        style={{
                          color:
                            "#64748b",
                          fontSize:
                            "14px",
                        }}
                      >
                        Instalment{" "}
                        {nextInstallment}{" "}
                        of{" "}
                        {TOTAL_INSTALLMENTS}
                      </span>

                      <strong
                        style={{
                          color:
                            "#111827",
                          fontSize:
                            "22px",
                        }}
                      >
                        ₹
                        {formatINR(
                          INSTALLMENT_AMOUNT
                        )}
                      </strong>

                    </div>

                  </div>

                </div>
              )}

              {isCompleted && (
                <div
                  style={{
                    marginTop: "20px",
                    padding: "22px",
                    background:
                      "#f0fdf4",
                    border:
                      "1px solid #bbf7d0",
                    borderRadius:
                      "7px",
                    textAlign:
                      "center",
                  }}
                >

                  <div
                    style={{
                      fontSize:
                        "32px",
                      marginBottom:
                        "8px",
                    }}
                  >
                    ✓
                  </div>

                  <strong
                    style={{
                      display:
                        "block",
                      color:
                        "#166534",
                      fontSize:
                        "18px",
                    }}
                  >
                    All 3 Instalments Completed
                  </strong>

                  <p
                    style={{
                      margin:
                        "8px 0 0",
                      color:
                        "#166534",
                      fontSize:
                        "14px",
                    }}
                  >
                    ₹
                    {formatINR(
                      TOTAL_PRICE
                    )}{" "}
                    paid successfully.
                  </p>

                </div>
              )}

            </>
          )}

          {/* ORDER SUMMARY */}

          {!isCompleted && (
            <div className="ascend-order-card">

              <div className="ascend-order-top-line" />

              <div className="ascend-order-content">

                <h3>
                  Order summary
                </h3>

                <div className="ascend-service-row">

                  <span>
                    Forge Your Peak –
                    Ascend
                  </span>

                  <strong>
                    ₹
                    {formatINR(
                      installmentData &&
                      !isCompleted
                        ? INSTALLMENT_AMOUNT
                        : ORIGINAL_PRICE
                    )}
                  </strong>

                </div>

                <div className="ascend-divider" />

                <div className="ascend-total-row">

                  <span>
                    Amount to be paid
                  </span>

                  <strong>
                    ₹
                    {formatINR(
                      installmentData &&
                      !isCompleted
                        ? INSTALLMENT_AMOUNT
                        : ORIGINAL_PRICE
                    )}
                  </strong>

                </div>

              </div>

            </div>
          )}

          {/* PROCEED TO PAY */}

          {!isCompleted && (
            <button
              type="button"
              className="ascend-pay-button"
              onClick={
                handlePayment
              }
              disabled={
                isLoading ||
                isCheckingInstallment ||
                !installmentData
              }
            >
              {isLoading
                ? "Processing..."
                : installmentData
                ? `Proceed to Pay ₹${formatINR(
                    INSTALLMENT_AMOUNT
                  )}`
                : "Check Instalment to Continue"}
            </button>
          )}

          {/* PAYMENT METHODS */}

          {!isCompleted && (
            <div className="ascend-payment-methods">
              <span>
                Secure payment powered by Razorpay
              </span>
            </div>
          )}

        </div>

      </section>

    </div>
  );
};

export default AscendInstallment;